
import { Component, Prop, Provide, Mixins } from "vue-property-decorator";
import { IFunctionality, IFunctionalityCategory } from "@/store/types";
import RequestPromises from "@/mixins/requestPromises";
import { IUserGroup } from "@/models/userGroups";
import { mapState } from "vuex";
import { IState } from "@/store";
import { DataModules } from "@/store/modules/data";
import _ from "@/boot/lodash";
import { CreateElement } from "vue";
import { IProvider } from "@/models/providers";
import {
  IUserGroupProvider,
  IUserGroupProviderMethods
} from "@/models/providers/userGroups";

@Component({
  computed: {
    ...mapState({
      userGroup(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.USER_GROUPS,
          scope: this.userGroupScope
        });
      },
      userGroupScope(state: IState, getters) {
        return getters[`data/${DataModules.USER_GROUPS}/scope`](
          this.userGroupCode
        );
      }
    })
  }
})
export default class UserGroupsProvider extends Mixins(RequestPromises)
  implements IProvider {
  // Props

  @Prop({ default: "" })
  public userGroupCode!: string;

  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  // Data

  public userGroup!: IUserGroup;
  public userGroupScope!: string;
  public functionalityCategories: IFunctionalityCategory[] = [];
  public isLoading = true;
  public isReloading = false;
  public isProcessing = false;
  public hasRequestError = false;

  // Life-cycle methods
  public async created() {
    if (!this.canTouchForm) {
      return (this.isLoading = false);
    }
    this.loadData();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binSingle", {
      storeModule: DataModules.USER_GROUPS,
      scope: this.userGroupScope,
      vm: this
    });
  }

  // Computed

  public get exists() {
    return this.userGroupCode.length > 0;
  }

  public get canCreate() {
    return !this.exists && this.$userCan("create_user_group");
  }

  public get canView() {
    return this.exists && this.$userCan("get_user_group");
  }

  public get canEdit() {
    return this.exists && this.$userCan("update_user_group");
  }

  public get canSubmit() {
    return this.canEdit || this.canCreate;
  }

  public get canListFunctionalities() {
    return this.$userCan("list_functionalities");
  }

  public get canTouchForm() {
    return this.canListFunctionalities && (this.canCreate || this.canView);
  }

  // Methods

  public async loadData() {
    try {
      this.isLoading = true;
      if (this.canEdit) {
        await this.getUserGroup();
      }
      if (this.canListFunctionalities) {
        await this.getFunctionalityCategories();
      }
      this.hasRequestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
      this.hasRequestError = true;
    } finally {
      this.isLoading = false;
      this.$emit("loaded");
    }
  }

  public async reloadData() {
    try {
      this.isReloading = true;
      await this.$store.commit("api/cancelRequests", this.requestPromises);
      await this.loadData();
    } finally {
      this.isReloading = false;
    }
  }

  public async getUserGroup() {
    if (!this.exists) {
      return Promise.resolve();
    }

    return this.$store.dispatch(`data/${DataModules.USER_GROUPS}/get`, {
      vm: this,
      scope: this.userGroupScope,
      id: this.userGroupCode
    });
  }

  private async getFunctionalityCategories() {
    const functionalities: IFunctionality[] = _.sortBy(
      await this.$store.dispatch(
        `data/${DataModules.USER_GROUPS}/getFunctionalities`,
        {
          params: {
            "filter[type]": "0"
          }
        }
      ),
      ["category.name", "name"]
    );
    const categories: IFunctionalityCategory[] = [];
    _.forEach(functionalities, functionality => {
      if (!_.get(categories, functionality.category.id, false)) {
        categories[functionality.category.id] = functionality.category;
        categories[functionality.category.id].functionalities = [];
      }
      categories[functionality.category.id].functionalities.push(functionality);
    });
    this.functionalityCategories = _.values(categories);
    return Promise.resolve();
  }

  private save(form) {
    if (this.exists) {
      return this.$store.dispatch(`data/${DataModules.USER_GROUPS}/update`, {
        id: this.userGroup.code,
        data: form
      });
    }

    return this.$store.dispatch(`data/${DataModules.USER_GROUPS}/create`, {
      data: form
    });
  }

  public toggleAll(
    form: { functionalities: string[] },
    categoryId: IFunctionalityCategory["id"]
  ) {
    const category = _.find(
      this.functionalityCategories,
      (functionalityCategory: IFunctionalityCategory) => {
        return functionalityCategory.id === categoryId;
      }
    );
    if (!category) {
      return;
    }
    const functionalities = _.map(category.functionalities, "code");

    if (this.hasAll(form, category)) {
      form.functionalities = form.functionalities.filter(
        el => !functionalities.includes(el)
      );

      return;
    }

    form.functionalities = _.union(form.functionalities, functionalities);
  }

  public hasAll(
    form: { functionalities: string[] },
    category: IFunctionalityCategory
  ): boolean {
    const functionalities = _.map(category.functionalities, "code");

    return _.difference(functionalities, form.functionalities).length === 0;
  }

  @Provide("userGroupProvider")
  public providerData(): IUserGroupProvider {
    return {
      userGroup: this.userGroup,
      functionalityCategories: this.functionalityCategories,
      exists: this.exists,
      canCreate: this.canCreate,
      canView: this.canView,
      canEdit: this.canEdit,
      canSubmit: this.canSubmit,
      canTouchForm: this.canTouchForm,
      canListFunctionalities: this.canListFunctionalities,
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      hasRequestError: this.hasRequestError
    };
  }

  @Provide("userGroupProviderMethods")
  public get providerMethods(): IUserGroupProviderMethods {
    return {
      getFunctionalityCategories: this.getFunctionalityCategories,
      save: this.save,
      getUserGroup: this.getUserGroup,
      reloadData: this.reloadData,
      toggleAll: this.toggleAll,
      hasAll: this.hasAll
    };
  }

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });
    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $userGroupData: this.providerData(),
        $userGroupMethods: this.providerMethods
      })
    );
  }
}
